import { useState } from 'react';
import { useIntl } from 'react-intl';
import {
  useLoaderData,
  useNavigate,
  useRevalidator,
  useRouteLoaderData,
  type LoaderFunctionArgs,
} from 'react-router-dom';
import { Button, Modal } from '@immo/ui-kit';
import type { AdminMeResponse, GetAdminResponse } from '@immo/api-contract';
import { apiClient } from '@/lib/auth/api-client';
import { RequirePermission } from '@/lib/auth/permissions';
import { useTranslations } from '@/i18n/use-translations';
import { BreadcrumbLeading } from '@/components/layout/page-leading';
import { PageTopbar } from '@/components/layout/topbar-slots';
import { ShieldIcon } from '@/components/layout/icons';
import { ResourceHeader } from '@/components/resource-header';
import { StatusBadge } from '@/pages/admins';

const PROTECTED_ROUTE_ID = 'admin-protected';

function useMe(): AdminMeResponse | null {
  const data = useRouteLoaderData(PROTECTED_ROUTE_ID) as AdminMeResponse | undefined;

  return data ?? null;
}

export async function adminDetailLoader({ params }: LoaderFunctionArgs): Promise<GetAdminResponse> {
  const id = params['id'];

  if (typeof id !== 'string' || id.length === 0) {
    throw new Response('Missing admin id', { status: 404 });
  }

  const result = await apiClient.adminAdmins.get({ pathParams: { id } });

  if (!result.ok) {
    throw new Response('Failed to load admin', { status: result.status });
  }

  return result.body;
}

function SectionHeading({ children }: { children: React.ReactNode }) {
  return <h2 className="text-sm font-semibold uppercase tracking-wide text-text-tertiary">{children}</h2>;
}

export function AdminDetailPage() {
  const t = useTranslations('pages.admin.admins');
  const intl = useIntl();
  const admin = useLoaderData() as GetAdminResponse;
  const navigate = useNavigate();
  const revalidator = useRevalidator();
  const me = useMe();

  const isSelf = me !== null && me.user.email.trim().toLowerCase() === admin.email.trim().toLowerCase();
  const canDeactivate = admin.status === 'active' && !isSelf;

  const activeKeys = admin.activeAdminKeys;
  const warnsAboutKeys = activeKeys.length > 0;

  const [confirmOpen, setConfirmOpen] = useState(false);
  const [deactivating, setDeactivating] = useState(false);
  const [deactivateError, setDeactivateError] = useState<string | null>(null);

  const closeConfirm = () => {
    if (deactivating) return;
    setConfirmOpen(false);
    setDeactivateError(null);
  };

  const confirmDeactivate = async () => {
    setDeactivating(true);
    setDeactivateError(null);

    try {
      const result = await apiClient.adminAdmins.deactivate({ pathParams: { id: admin.id }, body: {} });

      if (result.ok) {
        setConfirmOpen(false);
        revalidator.revalidate();

        return;
      }

      setDeactivateError(deactivateErrorMessage(result, t));
    } catch {
      setDeactivateError(t('deactivate-generic-error'));
    } finally {
      setDeactivating(false);
    }
  };

  return (
    <>
      <PageTopbar
        leading={
          <BreadcrumbLeading
            icon={ShieldIcon}
            crumbs={[{ label: t('title'), to: '/admins' }, { label: admin.fullName }]}
          />
        }
        trailing={
          canDeactivate ? (
            <RequirePermission permission="backoffice:admin:deactivate">
              <Button size="sm" variant="danger" onClick={() => setConfirmOpen(true)}>
                {t('deactivate-cta')}
              </Button>
            </RequirePermission>
          ) : null
        }
      />
      <ResourceHeader icon={ShieldIcon} title={admin.fullName} meta={admin.email} />
      <div className="space-y-8 px-6 pb-8">
        <section className="space-y-2">
          <dl className="grid grid-cols-[180px_1fr] gap-y-2 text-base">
            <dt className="text-text-tertiary">{t('columns.email')}</dt>
            <dd className="text-text-primary">{admin.email}</dd>
            <dt className="text-text-tertiary">{t('columns.status')}</dt>
            <dd>
              <StatusBadge status={admin.status} label={t(`status.${admin.status}`)} />
            </dd>
            <dt className="text-text-tertiary">{t('columns.created-at')}</dt>
            <dd className="text-text-primary">{intl.formatDate(admin.createdAt, { dateStyle: 'long' })}</dd>
            <dt className="text-text-tertiary">{t('columns.created-by')}</dt>
            <dd className="text-text-primary">{admin.createdByFullName ?? t('created-by-system')}</dd>
            <dt className="text-text-tertiary">{t('detail.id-label')}</dt>
            <dd className="font-mono text-sm text-text-primary">{admin.id}</dd>
          </dl>
        </section>
        <section className="space-y-2">
          <SectionHeading>{t('api-keys.title')}</SectionHeading>
          {/*
            A admin API key is bound to an operator, so this is where an operator
            answers "what dies when I deactivate this colleague" (IMM-587.6).
          */}
          <RequirePermission permission="backoffice:identity:api-key-read">
            <Button variant="secondary" size="sm" onClick={() => navigate(`/admins/${admin.id}/api-keys`)}>
              {t('api-keys.view-cta')}
            </Button>
          </RequirePermission>
        </section>
        <section className="space-y-2">
          <SectionHeading>{t('detail.back-cta-heading')}</SectionHeading>
          <Button variant="secondary" size="sm" onClick={() => navigate('/admins')}>
            {t('detail.back-cta')}
          </Button>
        </section>
      </div>
      {confirmOpen && (
        <Modal
          open
          onClose={closeConfirm}
          confirmOnClose={false}
          size="md"
          title={warnsAboutKeys ? t('deactivate-keys-confirm-title') : t('deactivate-confirm-title')}
          footer={
            <>
              <Button variant="ghost" type="button" onClick={closeConfirm} disabled={deactivating}>
                {t('deactivate-cancel-cta')}
              </Button>
              <Button type="button" variant="danger" onClick={confirmDeactivate} disabled={deactivating}>
                {deactivating
                  ? t('deactivate-loading')
                  : warnsAboutKeys
                    ? t('deactivate-keys-confirm-cta')
                    : t('deactivate-confirm-cta')}
              </Button>
            </>
          }
        >
          {/*
            `break-words` because a key name is free text: an unbroken run would
            otherwise overflow the dialog rather than wrap (BL-042).
          */}
          <p className="break-words text-sm text-text-secondary">
            {warnsAboutKeys
              ? t('deactivate-keys-confirm-body', {
                  count: activeKeys.length,
                  names: intl.formatList(
                    activeKeys.map((key) => key.name),
                    { type: 'conjunction' },
                  ),
                })
              : t('deactivate-confirm-body', { fullName: admin.fullName, email: admin.email })}
          </p>
          {deactivateError !== null && <p className="mt-3 text-sm text-danger-600">{deactivateError}</p>}
        </Modal>
      )}
    </>
  );
}

function deactivateErrorMessage(
  result: { readonly status: number; readonly body?: unknown },
  t: (key: string) => string,
): string {
  if (result.status === 403) return t('deactivate-self-error');

  const body = result.body as { error?: { code?: string } } | undefined;
  const code = body?.error?.code;

  if (code === 'admin.admin.cannot-deactivate-self') return t('deactivate-self-error');

  if (code === 'admin.admin.last-active-admin') return t('deactivate-last-error');

  return t('deactivate-generic-error');
}
